import { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useIntl } from 'react-intl';
import { IssueOnSingleView } from 'components/dataProviders/withIssue/IssueOnSingleView';
import { IssueColumnsFactory } from '../columns';
import { useProcessesObject } from 'store/process';
import { useProject } from 'store/project';
import { useCurrentUser } from 'store/user';
import { useIssueDataGridSlotProps } from './useIssueDataGridSlotProps';
import { useVisibleFieldsByProcess } from 'store/visibleFields';
import { toVisibleFieldModelMap } from 'shared/domain/visibleField/visibleFieldsMap';
import { getIssueStaticFilterPresets } from '../filterPresets';

export function useIssueGridColumnsConfig(): {
  columns: GridColDef<IssueOnSingleView>[];
  slotProps: ReturnType<typeof useIssueDataGridSlotProps>;
} {
  const intl = useIntl();
  const locale = intl.locale;
  const { timezone, organizationId, _id: projectId } = useProject();
  const { _id: currentUserId } = useCurrentUser();
  const processes = useProcessesObject();
  const visibleFields = useVisibleFieldsByProcess();
  const visibleFieldsMap = useMemo(
    () => toVisibleFieldModelMap(visibleFields),
    [visibleFields]
  );

  const columns = useMemo(
    () =>
      new IssueColumnsFactory(
        intl,
        timezone ?? '',
        organizationId ?? '',
        processes,
        locale,
        visibleFieldsMap,
        projectId ?? ''
      ).create(),
    [locale, timezone, organizationId, projectId, processes, visibleFieldsMap]
  );

  const staticFilterPresets = useMemo(
    () => getIssueStaticFilterPresets(intl, currentUserId),
    [intl, currentUserId]
  );
  const slotProps = useIssueDataGridSlotProps({ staticFilterPresets });

  return { columns, slotProps };
}
